import { Component, OnDestroy, OnInit } from '@angular/core';
import { ConfigService } from './app-config.service';
import { StorageService } from '@services/storage/storage.service';
import { Observable, Subject, filter, merge, takeUntil, withLatestFrom } from 'rxjs';
import { UserAction, UserState, UserStatus } from '@state/user.state';
import { Select, Store } from '@ngxs/store';
import { UserClaims } from './@types/dto/user-claims.model';
import { AuthenticationService } from './auth/service';
import { QuestionnaireAction } from '@state/questionnaire.state';
import { CookieConsentService } from '@services/common/cookie.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { LoaderService } from '@services/loader/loader.service';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { OrganizationAction } from '@state/organization.state';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OnboardingComponent } from './main/apps/components/onboarding/onboarding.component';
import { PostHogService } from '@services/common/post-hog.service';
import { ConsentService } from '@services/common/consent.service';
import { User } from './auth/models';
import { QuestionnaireConfigAction } from '@state/questionnaire.config.state';
import { ResultWebSocketService } from './main/services/datasources/result-web-socket.service';
import { EmissionFactorItemsAction } from '@state/organizationItems.state';
import { AuthSessionTransitionService } from './auth/service/auth-session-transition.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    providers: [DialogService],
    standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims>;
    @Select(UserState.getUserStatus) userStatus$!: Observable<UserStatus>;
    @Select(UserState.getCurrentUser) user$!: Observable<User>;

    private ngUnsubscribe = new Subject<void>();
    private readonly appSessionTeardown$: Observable<void>;
    // @ts-expect-error: TODO: Remove ignore when types are fixed
    public userClaim: UserClaims;

    //Onboarding
    public isOrgOnboarded: boolean = false;
    public ref: DynamicDialogRef | undefined;

    constructor(
        private configService: ConfigService,
        private cookieConsentService: CookieConsentService,
        private postHogService: PostHogService,
        private consentService: ConsentService,
        private storageService: StorageService,
        private translateService: CustomTranslateService,
        private authenticationService: AuthenticationService,
        private store: Store,
        private titleService: Title,
        private router: Router,
        private loader: LoaderService,
        private questionnaireService: QuestionnaireHttpService,
        public dialogService: DialogService,
        private resultWebSocketService: ResultWebSocketService,
        private readonly authSessionTransition: AuthSessionTransitionService,
    ) {
        this.appSessionTeardown$ = merge(this.ngUnsubscribe, authSessionTransition.sessionTeardown$);
    }

    ngOnInit(): void {
        // Analytics is driven purely by the cookie banner: no consent, no SDK.
        this.consentService.activate();
        this.consentService.analyticsConsent$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((granted) => this.postHogService.setAnalyticsConsent(granted));

        // Drop the identified person on logout so the next user signing in on
        // this browser is not merged into the previous one.
        this.authSessionTransition.sessionTeardown$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.postHogService.reset());

        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationStart || e instanceof NavigationEnd),
                takeUntil(this.ngUnsubscribe),
                withLatestFrom(this.user$),
            )
            .subscribe(([e, user]) => {
                this.loader.setLoading(e instanceof NavigationStart);
                if (this.userClaim && this.userClaim.organization) {
                    this.isOrgOnboarded = this.userClaim.organization.isOnboarded ?? false;
                    if (!this.isOrgOnboarded && this.ref === undefined && user.has_accepted_terms === '1') {
                        this.openOnboardingModal();
                    }
                }
            });

        this.translateService.get('common.title').subscribe((title) => {
            if (title) {
                this.titleService.setTitle(title as string);
            }
        });

        this.authenticationService.currentUser$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            if (user) {
                this.store.dispatch(new UserAction.Activate());
            }
        });

        this.userClaims$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((claims) => {
            this.userClaim = claims;
            this.handleUserClaims();
        });

        this.cookieConsentService.updateCookiePopup();
    }

    handleUserClaims() {
        if (this.userClaim) {
            this.postHogService.identify(this.userClaim);

            // If the user is a dcarb admin, we need to initialize the emission factor databases.
            const dcarbAdminTask = this.userClaim.tasks.find((task) => task.name === 'dcarb_admin');
            if (dcarbAdminTask && !dcarbAdminTask.organization_id) {
                this.store.dispatch(new EmissionFactorItemsAction.SetEmissionFactorDatabases());
            }
            if (this.userClaim.organization?.uid && this.userClaim.organization_entities) {
                this.store.dispatch(new OrganizationAction.Activate(this.userClaim.organization.uid));
                this.questionnaireService
                    .getAll(this.userClaim.organization?.uid)
                    .pipe(takeUntil(this.appSessionTeardown$))
                    .subscribe((questionnairesQuestionInstances) => {
                        const questionnaires = questionnairesQuestionInstances
                            .map(({ questionnaire }) => questionnaire)
                            .filter((quest) => !quest.deleted);
                        const sortedquestionaires = questionnaires.sort(
                            (a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime(),
                        );
                        this.store.dispatch(
                            new QuestionnaireAction.Activate(
                                // @ts-expect-error: TODO: Remove ignore when types are fixed
                                this.userClaim.organization?.uid,
                                sortedquestionaires[0].refYear,
                                sortedquestionaires[0].organizationEntityId,
                            ),
                        );
                        this.store.dispatch(
                            new QuestionnaireConfigAction.Activate(
                                // @ts-expect-error: TODO: Remove ignore when types are fixed
                                this.userClaim.organization?.uid,
                                sortedquestionaires[0].organizationEntityId,
                            ),
                        );
                    });
            }
        }
    }

    private openOnboardingModal() {
        const element = document.getElementById('language-selector');
        if (element) {
            element.style.display = 'none';
        }

        this.ref =
            this.dialogService.open(OnboardingComponent, {
                // The wizard draws its own title; it cannot be closed before it is done.
                showHeader: false,
                closable: false,
                draggable: false,
                styleClass: 'onboarding-modal dialog-xl',
            }) ?? undefined;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
